import { useNavigate, useLocation } from "react-router-dom";
import { MessageCircle, Home, Calendar, MapPin, Sparkles, User, Mail, Phone, ClipboardList, CreditCard, PhoneCall } from "lucide-react";
import { useEffect } from "react";
import { useTracking, trackMetaEvent, trackGA4Event } from "@/hooks/useTracking";
import { formatEventDateFr, formatPrice } from "@/utils/dateFormat";
import { buildOptionRows } from "@/utils/optionsCatalog";

/* ——— Design tokens (rose gold) ——— */
const GOLD = "#B8977E";
const GOLD_LIGHT = "#F5EDE8";
const DARK = "#1A1A1A";
const MUTED = "#6B6B6B";
const BORDER = "#F0E6DC";
const BG_NEUTRAL = "#FAFAF8";
const WHATSAPP = "#25D366";

/* ——— Small building blocks ——— */
const InfoRow = ({ icon: Icon, children, testid }) => (
  <p data-testid={testid} style={{ margin: "0 0 6px 0", fontSize: 15, color: DARK, display: "flex", alignItems: "center", gap: 10, lineHeight: 1.55 }}>
    <Icon size={16} style={{ color: GOLD, flexShrink: 0 }} />
    <span>{children}</span>
  </p>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <h2 style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 14px 0", fontFamily: "'Playfair Display',Georgia,serif", fontSize: 20, color: DARK, fontWeight: 600 }}>
    <Icon size={20} style={{ color: GOLD }} />
    {children}
  </h2>
);

const Divider = () => (
  <hr style={{ border: 0, borderTop: `1px solid ${BORDER}`, margin: "14px 0" }} />
);

const LineItem = ({ label, value, muted = false, strong = false }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, margin: "4px 0", fontSize: strong ? 22 : 15, color: muted ? MUTED : DARK, fontFamily: strong ? "'Playfair Display',Georgia,serif" : undefined, fontWeight: strong ? 600 : undefined }}>
    <span>{label}</span>
    <span style={{ color: strong ? GOLD : "inherit", whiteSpace: "nowrap" }}>{value}</span>
  </div>
);

/* ——— Page ——— */
const ThankYouPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const booking = state.booking || {};
  useTracking({ page: "thank-you" });

  useEffect(() => {
    trackMetaEvent("CompleteRegistration", { content_name: "Devis Photobooth Signé", status: true });
    trackGA4Event("conversion", { event_category: "lead", event_label: "devis_signe" });
  }, []);

  const firstName = state.firstName || (booking.customer_name || "").split(" ")[0] || "";
  const eventDateIso = booking.event_date || state.eventDate;
  const eventDateFr = formatEventDateFr(eventDateIso);
  const eventDateTimeFr = formatEventDateFr(eventDateIso, booking.event_start_time);
  const signedDateFr = eventDateFr;

  const optionRows = buildOptionRows(booking.options);
  const optionsTotal = optionRows.reduce((s, r) => s + r.price, 0);
  const deliverySurcharge = Number(booking.delivery_surcharge || 0);
  const total = Number(booking.total_price ?? state.total ?? 0);
  const packagePriceOriginal = Number(booking.package_price || 0);

  // Discount reconstruction (best-effort — the booking model stores promotion type/value but not amount)
  let promoLine = null;
  if (booking.promotion && booking.promotion.value && packagePriceOriginal > 0) {
    const discount = booking.promotion.type === "percentage"
      ? (packagePriceOriginal * booking.promotion.value) / 100
      : booking.promotion.value;
    if (discount > 0) {
      promoLine = { title: booking.promotion.title, amount: discount };
    }
  }
  const subtotal = packagePriceOriginal + optionsTotal + deliverySurcharge;

  const whatsappUrl = `https://wa.me/33679393429?text=${encodeURIComponent(`Bonjour Walid, j'ai une question sur ma réservation${eventDateFr ? " du " + eventDateFr : ""}.`)}`;

  return (
    <div style={{ minHeight: "100vh", background: BG_NEUTRAL, padding: "3rem 1rem", fontFamily: "'DM Sans',Arial,sans-serif" }}>
      <div
        data-testid="thank-you-container"
        style={{
          maxWidth: 700,
          margin: "0 auto",
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 4px 24px rgba(184,151,126,0.10)",
          overflow: "hidden",
        }}
      >
        {/* ——— Header : check + title ——— */}
        <div style={{ padding: "3rem 2rem 2rem", textAlign: "center" }}>
          <svg width="80" height="80" viewBox="0 0 80 80" style={{ margin: "0 auto 1.5rem", display: "block" }}>
            <circle cx="40" cy="40" r="36" fill="none" stroke={GOLD} strokeWidth="3" opacity="0.25" />
            <circle cx="40" cy="40" r="36" fill="none" stroke={GOLD} strokeWidth="3" strokeDasharray="226" style={{ animation: "checkDraw 0.8s ease forwards" }} />
            <path d="M24 42 L35 53 L56 30" fill="none" stroke={GOLD} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="48" style={{ animation: "checkDraw 0.8s ease 0.3s forwards" }} />
          </svg>
          <style>{`@keyframes checkDraw { from { stroke-dashoffset: 226; } to { stroke-dashoffset: 0; } }`}</style>

          <h1 data-testid="thank-you-title" style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: "clamp(1.75rem, 3vw, 2rem)", fontWeight: 700, color: DARK, margin: "0 0 0.75rem 0", lineHeight: 1.25 }}>
            Votre réservation est confirmée
          </h1>
          <p style={{ color: MUTED, fontSize: "0.95rem", lineHeight: 1.7, margin: 0 }}>
            Merci {firstName || "à vous"}, votre devis a bien été signé{signedDateFr ? <> pour le <strong style={{ color: DARK }}>{signedDateFr}</strong></> : ""}.<br />
            Un email de confirmation vient de vous être envoyé.
          </p>
        </div>

        {/* ——— Récap ——— */}
        {(booking.package_name || booking.customer_name) && (
          <div style={{ padding: "0 2rem" }}>
            <div
              data-testid="booking-recap"
              style={{
                background: GOLD_LIGHT,
                borderLeft: `4px solid ${GOLD}`,
                borderRadius: 12,
                padding: "20px 24px",
                textAlign: "left",
              }}
            >
              <SectionTitle icon={ClipboardList}>Récapitulatif de votre réservation</SectionTitle>

              {booking.package_name && (
                <p style={{ margin: "0 0 12px 0", fontFamily: "'Playfair Display',Georgia,serif", fontSize: 18, color: DARK, fontWeight: 600 }}>
                  {booking.package_name}
                </p>
              )}

              {eventDateTimeFr && <InfoRow icon={Calendar} testid="recap-date">{eventDateTimeFr}</InfoRow>}
              {booking.event_location && <InfoRow icon={MapPin} testid="recap-location">{booking.event_location}</InfoRow>}
              {booking.event_type && <InfoRow icon={Sparkles} testid="recap-event-type">{booking.event_type}</InfoRow>}

              {(booking.customer_name || booking.customer_email || booking.customer_phone) && <Divider />}
              {booking.customer_name && <InfoRow icon={User} testid="recap-customer-name">{booking.customer_name}</InfoRow>}
              {booking.customer_email && <InfoRow icon={Mail} testid="recap-customer-email">{booking.customer_email}</InfoRow>}
              {booking.customer_phone && <InfoRow icon={Phone} testid="recap-customer-phone">{booking.customer_phone}</InfoRow>}

              {optionRows.length > 0 && (
                <>
                  <Divider />
                  <p style={{ margin: "0 0 8px 0", fontSize: 13, color: MUTED, textTransform: "uppercase", letterSpacing: "0.06em" }}>Options sélectionnées</p>
                  {optionRows.map((r) => (
                    <LineItem key={r.key} label={`+ ${r.label}`} value={`+${formatPrice(r.price)}`} />
                  ))}
                </>
              )}

              <Divider />
              <LineItem label="Sous-total" value={formatPrice(subtotal)} />
              {promoLine && (
                <LineItem
                  label={`Code ${promoLine.title}`}
                  value={`-${formatPrice(promoLine.amount)}`}
                  muted
                />
              )}
              {deliverySurcharge > 0 && !optionRows.length && (
                <LineItem label="Livraison hors IDF" value={`+${formatPrice(deliverySurcharge)}`} muted />
              )}
              <div style={{ borderTop: `1px solid ${BORDER}`, marginTop: 10, paddingTop: 10 }}>
                <LineItem label="TOTAL" value={formatPrice(total)} strong />
              </div>
            </div>
          </div>
        )}

        {/* ——— Modalités paiement ——— */}
        <div style={{ padding: "1.25rem 2rem 0" }}>
          <div
            data-testid="payment-notice"
            style={{
              background: BG_NEUTRAL,
              border: `1px solid ${BORDER}`,
              borderLeft: `4px solid ${GOLD}`,
              borderRadius: 12,
              padding: "20px 24px",
              textAlign: "left",
            }}
          >
            <SectionTitle icon={CreditCard}>Modalités de paiement</SectionTitle>
            <p style={{ margin: "0 0 10px 0", fontSize: 15, lineHeight: 1.65, color: DARK }}>
              <strong>Aucun paiement immédiat.</strong> Le règlement{total ? <> de <strong style={{ color: GOLD }}>{formatPrice(total)}</strong></> : ""} s&apos;effectuera le jour de l&apos;événement (espèces, chèque ou virement).
            </p>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.65, color: MUTED, display: "flex", alignItems: "center", gap: 8 }}>
              <PhoneCall size={16} style={{ color: GOLD, flexShrink: 0 }} />
              Nous vous contacterons quelques jours avant pour finaliser les détails techniques.
            </p>
          </div>
        </div>

        {/* ——— CTA ——— */}
        <div style={{ padding: "1.5rem 2rem 2.5rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-cta"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              width: "100%",
              padding: "14px 20px",
              background: WHATSAPP,
              color: "#fff",
              textDecoration: "none",
              fontFamily: "'DM Sans',sans-serif",
              fontWeight: 600,
              fontSize: 15,
              borderRadius: 8,
              transition: "opacity .2s ease",
            }}
          >
            <MessageCircle size={18} /> Discuter sur WhatsApp
          </a>
          <button
            onClick={() => navigate("/")}
            data-testid="home-cta"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              width: "100%",
              padding: "14px 20px",
              background: "transparent",
              color: GOLD,
              border: `1.5px solid ${GOLD}`,
              borderRadius: 8,
              fontFamily: "'DM Sans',sans-serif",
              fontWeight: 600,
              fontSize: 15,
              cursor: "pointer",
            }}
          >
            <Home size={18} /> Retour à l&apos;accueil
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPage;
